import { DeeplyReadonly, Filter } from '@cubejs-client/core';
import { JSX, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetPartnerScoreYearQuarters } from '@/api/hcp/partnerscore';
import { isAllQuarters } from '@/api/hcp/partnerscore/utils/partnerScoreUtils';
import {
  buildYearQuarterTreeOptions,
  DateItem,
  treeValueToYearQuarter,
  yearQuarterToTreeValue,
} from '@/api/hcp/partnerscore/utils/yearQuarterTreeUtils';
import { useAdobeAnalytics } from '@/context/AdobeAnalyticsProvider';
import { translateDynamicKey } from '@/types/translation';
import { SelectItem } from '@/types/ui';
import { trackSiteSearch } from '@/utils/analytics/trackOnSiteSearch';
import useGetPartnerScoreSurveyList from '@api/hcp/partnerscore/useGetPartnerScoreSurveyList';
import useSessionStorage, { SessionStorageKey } from '@hooks/useSessionStorage';
import usePartnerScoreFilterUtils from './usePartnerScoreFilterUtils';

export type PartnerScoreFiltersStorage = {
  yearQuarter: string[];
  surveyTitle: string[];
};

export type FilterOption<T = number> = {
  value: T;
} & ({ label: JSX.Element; altLabel: string } | { label: string });

type FiltersWithPreviousPeriodsProps = {
  cubeNameArg: string;
  currentYear: number;
  currentQuarter: number;
};

export type UsePartnerScoreFilterReturn = {
  year: number;
  quarter: number;
  yearQuarterOptions: SelectItem<string>[];
  yearQuarterValue: string[];
  handleYearQuarterChange: (selected: string[]) => void;
  surveyIdList: Array<string>;
  surveyOptions: FilterOption<string>[];
  handleSurveyTitlesChange: (selectedValue: Array<string>) => void;
  setFilters: (filters: PartnerScoreFiltersStorage) => void;
  defaultFilters: PartnerScoreFiltersStorage;
  filtersWithPreviousPeriods: ({
    cubeNameArg,
    currentQuarter,
    currentYear,
  }: FiltersWithPreviousPeriodsProps) => DeeplyReadonly<Filter>[];
  filtersWithImmediatePreviousPeriods: ({
    cubeNameArg,
    currentQuarter,
    currentYear,
  }: FiltersWithPreviousPeriodsProps) => DeeplyReadonly<Filter>[];
  filtersWithCurrentPeriod: ({
    cubeNameArg,
    currentQuarter,
    currentYear,
  }: FiltersWithPreviousPeriodsProps) => DeeplyReadonly<Filter>[];
  filterQuarterDimensions: (dimensions: string[], quarter: number) => string[];
  filterSurveyList: (cubeName: string, surveyIdList: Array<string>) => Array<Filter>;
};

type UsePartnerScoreStoredFiltersReturn = {
  yearQuarter: string[];
  surveyTitle: string[];
  setYearQuarter: (updater: string[] | ((prev: string[]) => string[])) => void;
  setSurveyTitle: (updater: string[] | ((prev: string[]) => string[])) => void;
  setFilters: (filters: PartnerScoreFiltersStorage) => void;
};

function usePartnerScoreStoredFilters(): UsePartnerScoreStoredFiltersReturn {
  const { value: storedFilters, setValue: setStoredFilters } = useSessionStorage<PartnerScoreFiltersStorage>(
    SessionStorageKey.PARTNER_SCORE_FILTERS,
    { yearQuarter: [], surveyTitle: [] },
  );

  const setYearQuarter = useCallback(
    (updater: string[] | ((prev: string[]) => string[])) => {
      setStoredFilters(prev => ({
        ...prev,
        yearQuarter: typeof updater === 'function' ? updater(prev.yearQuarter ?? []) : updater,
      }));
    },
    [setStoredFilters],
  );

  const setSurveyTitle = useCallback(
    (updater: string[] | ((prev: string[]) => string[])) => {
      setStoredFilters(prev => ({
        ...prev,
        surveyTitle: typeof updater === 'function' ? updater(prev.surveyTitle ?? []) : updater,
      }));
    },
    [setStoredFilters],
  );

  const setFilters = useCallback(
    (filters: PartnerScoreFiltersStorage) => {
      setStoredFilters(filters);
    },
    [setStoredFilters],
  );

  return {
    yearQuarter: storedFilters.yearQuarter ?? [],
    surveyTitle: storedFilters.surveyTitle ?? [],
    setYearQuarter,
    setSurveyTitle,
    setFilters,
  };
}

type PartnerScoreOptions = {
  yearQuarterOptions: SelectItem<string>[];
  availableQuarters: Record<string, number[]>;
  latestYear: number | null;
};

function usePartnerScoreOptions(date: DateItem[]): PartnerScoreOptions {
  const safeDate = useMemo(() => date ?? [], [date]);

  const availableQuarters = useMemo(
    () =>
      safeDate.reduce(
        (acc, { year: yearItem, quarter: quarterItem }) => {
          if (!acc[yearItem]) {
            acc[yearItem] = [quarterItem];
          } else if (!acc[yearItem].includes(quarterItem)) {
            acc[yearItem].push(quarterItem);
          }
          return acc;
        },
        {} as Record<string, number[]>,
      ),
    [safeDate],
  );

  const latestYear = useMemo(() => {
    const years = Object.keys(availableQuarters).map(Number).filter(Boolean);
    return years.length ? Math.max(...years) : null;
  }, [availableQuarters]);

  const yearQuarterOptions = useMemo(() => buildYearQuarterTreeOptions(safeDate), [safeDate]);

  return { yearQuarterOptions, availableQuarters, latestYear };
}

function usePartnerScoreFilter(): UsePartnerScoreFilterReturn {
  const { yearQuarter, surveyTitle, setYearQuarter, setSurveyTitle, setFilters } = usePartnerScoreStoredFilters();

  // Derive year/quarter from the tree value for backward compatibility
  const parsed = useMemo(() => treeValueToYearQuarter(yearQuarter), [yearQuarter]);
  const year = parsed?.year ?? null;
  const quarter = parsed?.quarter ?? null;

  const { data: date = [], isSuccess: isDateSuccess } = useGetPartnerScoreYearQuarters();
  const { data: surveyTitlesList = [], isSuccess: isSurveyListSuccess } = useGetPartnerScoreSurveyList({
    year,
    quarter,
  });

  const { t } = useTranslation();
  const { trackLinkClick, trackOnSiteSearch, adobeAnalyticsConst } = useAdobeAnalytics();

  const { latestYear } = usePartnerScoreOptions(date);

  const autoSelectedDefaultRef = useRef<PartnerScoreFiltersStorage | null>(null);

  // Auto-select latest year when no selection exists
  useEffect(() => {
    if (!isDateSuccess || date.length === 0 || yearQuarter.length > 0) return;
    if (latestYear !== null) {
      const defaultYearQuarter = yearQuarterToTreeValue(latestYear, -1);
      autoSelectedDefaultRef.current = { yearQuarter: defaultYearQuarter, surveyTitle: [] };
      setYearQuarter(defaultYearQuarter);
    }
  }, [latestYear, date.length, isDateSuccess, yearQuarter.length, setYearQuarter]);

  const memoizedSurveyTitleSet = useMemo(
    () => new Set(surveyTitlesList.map(surveyItem => surveyItem.title)),
    [surveyTitlesList],
  );

  // Prune survey titles that no longer exist for the selected year/quarter.
  // Only prune after the user changes year/quarter (not on initial mount),
  // because on mount the survey list query may return stale cached results
  // before the correct query resolves.
  const prevYearQuarterRef = useRef(yearQuarter);
  const hasUserChangedYearQuarter = useRef(false);
  useEffect(() => {
    if (prevYearQuarterRef.current !== yearQuarter) {
      // First change on mount (from sessionStorage) is not a user change
      if (prevYearQuarterRef.current.length > 0 && yearQuarter.length > 0) {
        hasUserChangedYearQuarter.current = true;
      }
      prevYearQuarterRef.current = yearQuarter;
    }
  }, [yearQuarter]);

  useEffect(() => {
    if (!isSurveyListSuccess || memoizedSurveyTitleSet.size === 0) return;
    if (!hasUserChangedYearQuarter.current) return;
    setSurveyTitle(prev => {
      const filtered = prev.filter(title => memoizedSurveyTitleSet.has(title));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [memoizedSurveyTitleSet, isSurveyListSuccess, setSurveyTitle]);

  const handleYearQuarterChange = useCallback(
    (selected: string[]) => {
      if (!selected.length) return;
      setYearQuarter(selected);
      const parsedSelection = treeValueToYearQuarter(selected);
      if (!parsedSelection) return;
      const isQuarterLevel = !isAllQuarters(parsedSelection.quarter);
      const periodLabel = isQuarterLevel
        ? `Q${parsedSelection.quarter} ${parsedSelection.year}`
        : String(parsedSelection.year);
      const searchPos = translateDynamicKey(t, 'main.hcpOverview.partnerScore.filter.yearQuarter');
      trackSiteSearch({ value: periodLabel, trackOnSiteSearch, searchPos });
      trackLinkClick({
        innertext: isQuarterLevel ? periodLabel : `Y${parsedSelection.year}`,
        type: adobeAnalyticsConst.LINK_TYPES.BUTTON,
        position: adobeAnalyticsConst.POSITIONS.PARTNER_SCORE_FILTER,
        name: adobeAnalyticsConst.LINKCLICK_NAMES.PARTNER_SCORE_FILTERS,
      });
    },
    [adobeAnalyticsConst, trackLinkClick, trackOnSiteSearch, t, setYearQuarter],
  );

  const surveyOptions: Array<SelectItem<string>> = useMemo(
    () => surveyTitlesList.flatMap(({ title }) => (title ? [{ label: title, value: title }] : [])),
    [surveyTitlesList],
  );

  const handleSurveyTitlesChange = useCallback(
    (selectedValue: Array<string>) => setSurveyTitle(selectedValue),
    [setSurveyTitle],
  );

  const filterUtils = usePartnerScoreFilterUtils();

  const defaultFilters = autoSelectedDefaultRef.current ?? { yearQuarter: [], surveyTitle: [] };

  return {
    year,
    quarter,
    yearQuarterValue: yearQuarter,
    handleYearQuarterChange,
    surveyIdList: surveyTitle,
    surveyOptions,
    handleSurveyTitlesChange,
    setFilters,
    defaultFilters,
    ...filterUtils,
  };
}

export default usePartnerScoreFilter;
